// MUST be first: installs Array/String.prototype.at shims (and any future
// baseline polyfills) before any other module — app or vendor — can call them.
import './polyfills';
// Capture signed Teams consent in memory and scrub it before any other startup
// module, analytics client, router, or auth redirect can observe the URL.
import './containers/integrations/teams-clu-connect-state';
// Captures and removes one-time Slack callback tokens before Datadog, Clarity,
// PostHog, or application routing can observe the address bar.
import './lib/slack-callback-url';
// Same for Google's OAuth redirect (one-time `code` on /integrations/google/callback).
import { ThemedClerkProvider } from '@/components/themed-clerk-provider.component';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { AppIcon, IconStyleProvider } from '@/components/ui/app-icon';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  RouteError,
  RouteNotFound,
} from '@/containers/root/root-error.component';
import { WebSocketProvider } from '@/context/websocket-context';
import { initGoogleAds } from '@/lib/google-ads';
import { startHealthCheck } from '@/lib/health-check';
import { posthogCapturesHere } from '@/lib/posthog-capture';
import {
  maskPosthogNetworkRequest,
  redactPosthogEvent,
} from '@/lib/redact-emails';
import { initDatadog, reportError } from '@/lib/telemetry';
import {
  PRELOAD_RELOAD_FLAG,
  reloadOnStaleChunk,
} from '@/lib/stale-chunk-reload';
import { cn } from '@/lib/utils';
import setupLocatorUI from '@locator/runtime';
import Clarity from '@microsoft/clarity';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { PostHogProvider } from 'posthog-js/react';
import { Component, StrictMode } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import {
  iconCircleCheck,
  iconCircleInfo,
  iconCircleXmark,
  iconSpinnerThird,
  iconTriangleExclamation,
  iconXmark,
} from '@/lib/icons';
import './index.css';
import './lib/edge-scrollbar';
import { initGoogleTag } from './lib/google-tag';
import './lib/workspace-oauth-callback';
import { routeTree } from './routeTree.gen';
import { store } from './store';
import { registerRequestSimulator } from './store/base-query';
import { BRAND } from '@/lib/brand';

// The callback scrub above has completed before any module-body telemetry
// initialization. Google Ads used to load from index.html, where it could see
// the one-time Slack fragment before this entry module ran.
initGoogleTag();

// Stale-chunk recovery (CLEANLIST-PORTAL-63). Content-hashed chunk filenames
// change on every deploy. A user sitting on a cached index.html can request an
// old chunk hash that no longer exists on the CDN, so a lazy `import()` (e.g.
// the register route) rejects with "Failed to fetch dynamically imported
// module" — surfaced by Vite as a `vite:preloadError` event. Reloading the page
// pulls the fresh index.html + manifest and resolves it.
//
// The reload and its loop guard: src/lib/stale-chunk-reload.ts.
window.addEventListener('vite:preloadError', (event) =>
  reloadOnStaleChunk(event.payload?.message, () => window.location.reload())
);
// Clerk loads its sign-in UI from its own CDN with webpack, so a missed chunk
// there never fires vite:preloadError. It rejects with a ChunkLoadError
// instead, and the login/register page stays blank (Safari/iOS, PostHog
// issue 019e4cd9). Same recovery: one reload, but once per tab. Clerk mounts
// after the 5s clear below, so the shared guard alone could reload in a loop
// while Clerk's CDN stays unreachable.
const CLERK_CHUNK_RELOAD_FLAG = 'cleanlist:clerk-chunk-reloaded';
const reloadOnClerkChunkError = (error: unknown) => {
  if (!(error instanceof Error) || error.name !== 'ChunkLoadError') return;
  try {
    if (sessionStorage.getItem(CLERK_CHUNK_RELOAD_FLAG)) return;
    sessionStorage.setItem(CLERK_CHUNK_RELOAD_FLAG, '1');
  } catch {
    return; // no storage, no loop guard: let the error surface instead
  }
  reloadOnStaleChunk(error.message, () => window.location.reload());
};
window.addEventListener('unhandledrejection', (event) =>
  reloadOnClerkChunkError(event.reason)
);
window.addEventListener('error', (event) =>
  reloadOnClerkChunkError(event.error)
);
// Reached only if this module fully evaluated, i.e. the app's own chunk loaded
// fine — clear the flag so a future stale-chunk error can recover again. A
// flag this clear never reaches (the page unloaded first) only blocks the
// build it names, never a different one (see PRELOAD_RELOAD_FLAG).
setTimeout(() => {
  try {
    sessionStorage.removeItem(PRELOAD_RELOAD_FLAG);
  } catch {
    /* ignore — nothing to clear if storage is unavailable */
  }
}, 5000);

// Locator UI: Alt/Option-click any rendered element in dev to jump straight to
// its source (replaces code-inspector-plugin). Paired with the
// @locator/babel-jsx Babel plugin in vite.config.ts. Guarded on NODE_ENV so it
// is tree-shaken out of production builds.
if (process.env.NODE_ENV === 'development') {
  setupLocatorUI();
}

// Billing scenario simulator (src/dev/billing-simulator): the Billing pill
// bottom-right. Development only: a production build compiles this branch
// away, dynamic import included. It is registered before the first render,
// so no request can reach the API ahead of it: requests wait for the module,
// and pass through unless a scenario or "act as member" is on.
if (import.meta.env.DEV) {
  const simulator = import('./dev/billing-simulator').then(
    ({ installBillingSimulator }) => installBillingSimulator(store),
    (error: unknown) => {
      console.error('[billing simulator] failed to load; using the API', error);
      return null;
    }
  );
  registerRequestSimulator(async (args) => {
    const simulate = await simulator;
    return simulate ? simulate(args) : null;
  });
}

// Toast policy: there is deliberately no global suppression. An earlier
// transitional shim (`install-toast-policy.ts`) no-oped every toast.success /
// .info / .message / .loading and was silently swallowing hundreds of
// legitimate confirmation toasts, so it was disabled and has since been
// deleted. If specific toasts turn out to be noise, silence those individual
// call sites rather than reintroducing a global no-op.

// Telemetry stays off on localhost.
// *.localhost too: the Clu preview runs at app.clugtm.localhost.
const isLocalhost =
  ['localhost', '127.0.0.1', '::1', '[::1]'].includes(
    window.location.hostname
  ) || window.location.hostname.endsWith('.localhost');

if (!isLocalhost) {
  initDatadog();
}

if (!isLocalhost) {
  try {
    Clarity.init('u9p3nbqvz7');
  } catch {
    // Telemetry startup must never prevent the application from mounting.
  }
}

// Google Ads conversion tag. A no-op unless VITE_GOOGLE_ADS_ID is set.
if (!isLocalhost) {
  initGoogleAds();
}

// Last-resort boundary around the whole app: reports the crash and shows
// `fallback` until it is reset.
class RootErrorBoundary extends Component<
  {
    children: ReactNode;
    fallback: (props: { error: unknown; resetError: () => void }) => ReactNode;
  },
  { error: unknown; hasError: boolean }
> {
  state = { error: null as unknown, hasError: false };

  static getDerivedStateFromError(error: unknown) {
    return { error, hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    reportError(error, {
      tags: { surface: 'app_shell', boundary: 'root' },
      extra: { componentStack: info.componentStack },
    });
  }

  resetError = () => this.setState({ error: null, hasError: false });

  render() {
    return this.state.hasError
      ? this.props.fallback({
          error: this.state.error,
          resetError: this.resetError,
        })
      : this.props.children;
  }
}

const router = createRouter({
  routeTree,
  // Every page gets its own boundary inside the app shell, so a page that
  // throws keeps the sidebar and is reported. The root route's RootError
  // still covers a failure of the shell itself.
  defaultErrorComponent: RouteError,
  // An unknown URL gets the same in-shell treatment (see RouteNotFound).
  defaultNotFoundComponent: RouteNotFound,
  defaultOnCatch: (error, info) => {
    reportError(error, {
      tags: { surface: 'app_shell', boundary: 'route' },
      extra: { componentStack: info?.componentStack },
    });
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <Provider store={store}>
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
            // posthog-js gives up on /flags after 3s. On 2026-10-22 PostHog
            // answered in ~4s, so no browser got flags and every flag-gated
            // surface stayed hidden for new visitors. The page renders from
            // saved flags meanwhile, so a longer wait blocks nothing.
            feature_flag_request_timeout_ms: 10_000,
            capture_exceptions: true,
            // Lead emails in /people/<email> URLs stay out of analytics.
            before_send: redactPosthogEvent,
            // Replay records network bodies: keep Slack and Teams link tokens
            // (and lead emails) out of them and out of recorded URLs.
            session_recording: {
              maskCapturedNetworkRequestFn: maskPosthogNetworkRequest,
            },
            debug: import.meta.env.VITE_NODE_ENV !== 'production',
            // Off the production app (app.cleanlist.ai, app.clugtm.com), flags
            // still load but nothing is captured.
            opt_out_capturing_by_default: !posthogCapturesHere(),
            disable_session_recording: !posthogCapturesHere(),
          }}
        >
          <ThemedClerkProvider>
            <WebSocketProvider>
              <IconStyleProvider>
                <TooltipProvider delayDuration={0}>
                  <RootErrorBoundary
                    fallback={({ error, resetError }) => (
                      <div className='flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center'>
                        <h1 className='text-xl font-semibold text-foreground'>
                          Something went wrong
                        </h1>
                        <p className='max-w-md text-sm text-muted-foreground'>
                          The page hit an unexpected error. The {BRAND.name}{' '}
                          team has been notified. You can try reloading; if the
                          problem persists, contact support@cleanlist.ai.
                        </p>
                        {import.meta.env.DEV && (
                          <pre className='max-w-2xl overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground'>
                            {error instanceof Error
                              ? error.message
                              : String(error)}
                          </pre>
                        )}
                        <div className='flex gap-2'>
                          <button
                            onClick={resetError}
                            className={cn(
                              PRIMARY_BUTTON,
                              'px-4 py-2 text-sm font-medium'
                            )}
                          >
                            Try again
                          </button>
                          <button
                            onClick={() => window.location.reload()}
                            className='rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted'
                          >
                            Reload page
                          </button>
                        </div>
                      </div>
                    )}
                  >
                    <RouterProvider router={router} />
                  </RootErrorBoundary>
                  <Toaster
                    position='top-right'
                    closeButton
                    icons={{
                      success: <AppIcon icon={iconCircleCheck} />,
                      error: (
                        <AppIcon
                          icon={iconCircleXmark}
                          className='text-[var(--error-text)]'
                        />
                      ),
                      warning: <AppIcon icon={iconTriangleExclamation} />,
                      info: <AppIcon icon={iconCircleInfo} />,
                      loading: <AppIcon icon={iconSpinnerThird} spin />,
                      close: (
                        <AppIcon icon={iconXmark} mono className='size-3' />
                      ),
                    }}
                    toastOptions={{
                      classNames: {
                        closeButton:
                          'top-2! right-2! left-auto! transform-none! size-5! border-0! bg-transparent! text-[var(--normal-text)]! opacity-60 hover:bg-transparent! hover:opacity-100 focus-visible:opacity-100',
                        toast: 'items-center! pr-9!',
                      },
                    }}
                  />
                </TooltipProvider>
              </IconStyleProvider>
            </WebSocketProvider>
          </ThemedClerkProvider>
        </PostHogProvider>
      </Provider>
    </StrictMode>
  );
}

startHealthCheck();
